import {
  formatMoney,
  formatMoneyRaw,
  formatText,
  formatTextRaw,
  formatDate,
  toCsvLine,
  parseSections,
  renderStatementDocument,
  infoRow,
  infoRow2Col,
  infoTable2Col,
} from "../../../data/Reports/statementHtmlKit";

// Loan status / installment status -> badge color
const statusBadgeClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("arrear") || s.includes("default") || s.includes("npl")) {
    return "badge-red";
  }
  if (s.includes("clear") || s.includes("paid") || s.includes("active")) {
    return "badge-green";
  }
  if (s.includes("pending") || s.includes("due")) {
    return "badge-amber";
  }
  return "badge-grey";
};

const statusBadge = (status) =>
  `<span class="badge ${statusBadgeClass(status)}">${formatText(status)}</span>`;

export const buildLoanStatementHtml = ({ rawResults, generatedAt }) => {
  const sections = parseSections(rawResults);
  const header = sections.find((s) => s.Format === "header")?.Data || {};
  const summary = sections.find((s) => s.Format === "summary")?.Data || {};
  const scheduleRows = sections
    .filter((s) => s.Format === "table" && s.Section === "Repayment Schedule")
    .map((s) => s.Data);
  const paymentRows = sections
    .filter((s) => s.Format === "table" && s.Section === "Payment History")
    .map((s) => s.Data);
  const chargeRows = sections
    .filter((s) => s.Format === "table" && s.Section === "Charges & Fees")
    .map((s) => s.Data);

  const infoRowsHtml = infoTable2Col([
    infoRow2Col("Loan ID", formatText(header.LoanId), "Branch", formatText(header.Branch)),
    infoRow2Col("Customer Name", formatText(header.CustomerName), "Region", formatText(header.Region)),
    infoRow2Col("Customer ID", formatText(header.CustomerId), "Field Officer", formatText(header.FieldOfficer)),
    infoRow2Col("ID Number", formatText(header.IDNumber), "Application Date", formatText(header.ApplicationDate)),
    infoRow2Col("Phone", formatText(header.Phone), "Disbursement Date", formatText(header.DisbursementDate)),
    infoRow2Col("Product", formatText(header.ProductName), "Maturity Date", formatText(header.MaturityDate)),
    infoRow2Col("Loan Status", statusBadge(header.LoanStatus), "", ""),
  ].join(""));

  const summaryFields = [
    ["Loan Amount", summary.LoanAmount],
    ["Total Interest Charged", summary.TotalInterestCharged],
    ["Total Penalty Charged", summary.TotalPenaltyCharged],
    ["Total Amount Due", summary.TotalAmountDue],
    ["Total Repaid", summary.TotalRepaid],
    ["Principal Arrears", summary.PrincipalArrears],
    ["Interest Arrears", summary.InterestArrears],
  ];

  const summaryRowsHtml = summaryFields
    .map(
      ([label, value]) => `
          <tr>
            <td>${label}</td>
            <td class="amount">${formatMoney(value)}</td>
          </tr>`
    )
    .join("");

  const nextInstallmentHtml = `
    <div class="section">
      <h2 class="section-title">Arrears &amp; Next Installment</h2>
      <div class="info-box">
        ${infoRow("Arrears Overdue Days", formatText(summary.ArrearsOverdueDays))}
        ${infoRow(
          "Remaining Installments",
          formatText(summary.RemainingInstallments)
        )}
        ${infoRow("Next Installment Date", formatText(summary.NextInstallmentDate))}
        ${infoRow(
          "Next Installment Amount",
          `KES ${formatMoney(summary.NextInstallmentAmount)}`
        )}
      </div>
    </div>`;

  const scheduleRowsHtml = scheduleRows
    .map(
      (row) => `
          <tr>
            <td>${formatText(row.InstallmentNo)}</td>
            <td>${formatText(row.DueDate)}</td>
            <td class="amount">${formatMoney(row.PaidPrincipal)}</td>
            <td class="amount">${formatMoney(row.PaidInterest)}</td>
            <td class="amount">${formatMoney(row.PaidPenalty)}</td>
            <td class="amount">${formatMoney(row.PaidTotal)}</td>
            <td class="amount">${formatMoney(row.Remaining)}</td>
            <td>${statusBadge(row.Status)}</td>
          </tr>`
    )
    .join("");

  const paymentRowsHtml = paymentRows
    .map(
      (row) => `
          <tr>
            <td>${formatDate(row.PaymentDate)}</td>
            <td>${formatText(row.MpesaRef)}</td>
            <td class="amount">${formatMoney(row.AmountDue)}</td>
            <td class="amount">${formatMoney(row.Arrears)}</td>
            <td class="amount">${formatMoney(row.Prepaid)}</td>
            <td class="amount">${formatMoney(row.TotalCollected)}</td>
          </tr>`
    )
    .join("");

  const chargeRowsHtml = chargeRows
    .map(
      (row) => `
          <tr>
            <td>${formatDate(row.ChargeDate)}</td>
            <td>${formatText(row.ProductName)}</td>
            <td>${formatText(row.Description)}</td>
            <td class="amount">${formatMoney(row.Amount)}</td>
          </tr>`
    )
    .join("");

  const bodyHtml = `
    <div class="section">
      <h2 class="section-title">Loan Summary</h2>
      <table class="bordered-table">
        <thead>
          <tr><th>Metric</th><th style="text-align:right;">Amount (KES)</th></tr>
        </thead>
        <tbody>
          ${summaryRowsHtml}
          <tr class="total-row">
            <td>Outstanding Balance</td>
            <td class="amount">${formatMoney(summary.OutstandingBalance)}</td>
          </tr>
          <tr class="total-row">
            <td>Arrears</td>
            <td class="amount">${formatMoney(summary.Arrears)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    ${nextInstallmentHtml}

    <div class="section">
      <h2 class="section-title">Repayment Schedule</h2>
      <div class="table-wrap">
        <table class="bordered-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Due Date</th>
              <th style="text-align:right;">Principal</th>
              <th style="text-align:right;">Interest</th>
              <th style="text-align:right;">Penalty</th>
              <th style="text-align:right;">Total Paid</th>
              <th style="text-align:right;">Remaining</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${
              scheduleRowsHtml ||
              `<tr><td colspan="8" style="text-align:center;padding:16px;">No repayment schedule found.</td></tr>`
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Payment History</h2>
      <div class="table-wrap">
        <table class="bordered-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>M-Pesa Ref</th>
              <th style="text-align:right;">Amount Due</th>
              <th style="text-align:right;">Arrears</th>
              <th style="text-align:right;">Prepaid</th>
              <th style="text-align:right;">Total Collected</th>
            </tr>
          </thead>
          <tbody>
            ${
              paymentRowsHtml ||
              `<tr><td colspan="6" style="text-align:center;padding:16px;">No payments recorded.</td></tr>`
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">Charges &amp; Fees</h2>
      <div class="table-wrap">
        <table class="bordered-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Description</th>
              <th style="text-align:right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${
              chargeRowsHtml ||
              `<tr><td colspan="4" style="text-align:center;padding:16px;">No charges recorded.</td></tr>`
            }
          </tbody>
        </table>
      </div>
    </div>`;

  const csvSections = [];
  csvSections.push([toCsvLine(["Loan Statement"])]);
  csvSections.push([
    toCsvLine(["Loan ID", formatTextRaw(header.LoanId)]),
    toCsvLine(["Customer Name", formatTextRaw(header.CustomerName)]),
    toCsvLine(["Customer ID", formatTextRaw(header.CustomerId)]),
    toCsvLine(["ID Number", formatTextRaw(header.IDNumber)]),
    toCsvLine(["Phone", formatTextRaw(header.Phone)]),
    toCsvLine(["Product", formatTextRaw(header.ProductName)]),
    toCsvLine(["Branch", formatTextRaw(header.Branch)]),
    toCsvLine(["Region", formatTextRaw(header.Region)]),
    toCsvLine(["Field Officer", formatTextRaw(header.FieldOfficer)]),
    toCsvLine(["Application Date", formatTextRaw(header.ApplicationDate)]),
    toCsvLine(["Disbursement Date", formatTextRaw(header.DisbursementDate)]),
    toCsvLine(["Maturity Date", formatTextRaw(header.MaturityDate)]),
    toCsvLine(["Loan Status", formatTextRaw(header.LoanStatus)]),
  ]);
  csvSections.push([
    toCsvLine(["SUMMARY"]),
    toCsvLine(["Metric", "Amount (KES)"]),
    ...summaryFields.map(([label, value]) =>
      toCsvLine([label, formatMoneyRaw(value)])
    ),
    toCsvLine(["Outstanding Balance", formatMoneyRaw(summary.OutstandingBalance)]),
    toCsvLine(["Arrears", formatMoneyRaw(summary.Arrears)]),
    toCsvLine(["Arrears Overdue Days", formatTextRaw(summary.ArrearsOverdueDays)]),
    toCsvLine([
      "Remaining Installments",
      formatTextRaw(summary.RemainingInstallments),
    ]),
    toCsvLine(["Next Installment Date", formatTextRaw(summary.NextInstallmentDate)]),
    toCsvLine([
      "Next Installment Amount",
      formatMoneyRaw(summary.NextInstallmentAmount),
    ]),
  ]);

  csvSections.push([
    toCsvLine(["REPAYMENT SCHEDULE"]),
    toCsvLine([
      "InstallmentNo",
      "DueDate",
      "ExpectedPrincipal",
      "ExpectedInterest",
      "ExpectedPenalty",
      "ExpectedTotal",
      "PaidPrincipal",
      "PaidInterest",
      "PaidPenalty",
      "PaidTotal",
      "Remaining",
      "ClearedDate",
      "Status",
    ]),
    ...scheduleRows.map((row) =>
      toCsvLine([
        formatTextRaw(row.InstallmentNo),
        formatTextRaw(row.DueDate),
        formatMoneyRaw(row.ExpectedPrincipal),
        formatMoneyRaw(row.ExpectedInterest),
        formatMoneyRaw(row.ExpectedPenalty),
        formatMoneyRaw(row.ExpectedTotal),
        formatMoneyRaw(row.PaidPrincipal),
        formatMoneyRaw(row.PaidInterest),
        formatMoneyRaw(row.PaidPenalty),
        formatMoneyRaw(row.PaidTotal),
        formatMoneyRaw(row.Remaining),
        formatTextRaw(row.ClearedDate),
        formatTextRaw(row.Status),
      ])
    ),
  ]);

  csvSections.push([
    toCsvLine(["PAYMENT HISTORY"]),
    toCsvLine([
      "PaymentDate",
      "MpesaRef",
      "AmountDue",
      "Arrears",
      "Prepaid",
      "TotalCollected",
    ]),
    ...paymentRows.map((row) =>
      toCsvLine([
        formatTextRaw(row.PaymentDate),
        formatTextRaw(row.MpesaRef),
        formatMoneyRaw(row.AmountDue),
        formatMoneyRaw(row.Arrears),
        formatMoneyRaw(row.Prepaid),
        formatMoneyRaw(row.TotalCollected),
      ])
    ),
  ]);

  csvSections.push([
    toCsvLine(["CHARGES & FEES"]),
    toCsvLine(["ChargeDate", "Product", "Description", "Amount"]),
    ...chargeRows.map((row) =>
      toCsvLine([
        formatTextRaw(row.ChargeDate),
        formatTextRaw(row.ProductName),
        formatTextRaw(row.Description),
        formatMoneyRaw(row.Amount),
      ])
    ),
  ]);

  const csvContent = csvSections
    .map((lines) => lines.join("\r\n"))
    .join("\r\n\r\n");

  const csvFileName = `Loan_Statement_${(header.LoanId ?? "")
    .toString()
    .replace(/\s+/g, "_")}.csv`;

  return renderStatementDocument({
    docTitle: `Loan Statement - ${formatTextRaw(header.LoanId)}`,
    pageTitle: "Loan Account Statement",
    generatedAt,
    infoRowsHtml,
    bodyHtml,
    csvContent,
    csvFileName,
  });
};
